import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, CalendarClock, Lock, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

const fmt = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "—";
  }
};

export default function Verify() {
  const { fingerprint } = useParams();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let active = true;
    api
      .get(`/verify/${fingerprint}`)
      .then(({ data }) => active && setState({ loading: false, data }))
      .catch(() => active && setState({ loading: false, data: { verified: false } }));
    return () => {
      active = false;
    };
  }, [fingerprint]);

  const { loading, data } = state;
  const verified = data?.verified;
  const isCapsule = data?.type === "key_return_capsule";

  if (!loading && verified && isCapsule) {
    return (
      <div className="min-h-screen bg-[#EEF2F6]">
        <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4"><Logo /></div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="verify-page">
          <div className="rounded-2xl border border-[#BBF7D0] bg-white p-7 sm:p-9" data-testid="verify-capsule-verified">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#DCFCE7] text-[#15803D]"><ShieldCheck size={26} /></span>
              <div>
                <h1 className="font-heading text-2xl font-bold text-navy">Key Return Capsule</h1>
                <p className="text-sm text-[#475569]">Integrity verified · {data.status === "superseded" ? "Superseded version" : "Current version"}</p>
              </div>
            </div>
            <div className="mt-7 space-y-3">
              <Row icon={Lock} label="Sealed" value={fmt(data.sealed_at)} testId="verify-capsule-sealed" />
              <Row icon={CalendarClock} label="Capsule version" value={`v${data.capsule_version}`} testId="verify-capsule-version" />
              {data.property_ref ? <Row icon={MapPin} label="Property" value={data.property_ref} testId="verify-capsule-property" /> : null}
              <Row icon={ShieldCheck} label="Included photos" value={String(data.photo_count)} testId="verify-capsule-photos" />
              <Row icon={ShieldCheck} label="Supporting records" value={String(data.supporting_record_count)} testId="verify-capsule-records" />
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="verify-capsule-categories">
              {(data.included_record_categories || []).map((c) => (
                <span key={c} className="text-[10px] font-semibold text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] rounded px-2 py-0.5">{c.replace(/_/g, " ")}</span>
              ))}
            </div>
            <p className="mt-4 text-[11px] font-mono break-all text-[#94A3B8]" data-testid="verify-capsule-fingerprint">{data.manifest_fingerprint}</p>
            <p className="mt-6 text-xs leading-relaxed text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3" data-testid="verify-capsule-limitation">
              {data.limitation}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <Logo />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="verify-page">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-[#64748B]" data-testid="verify-loading">
            <Loader2 className="animate-spin" size={28} />
            <p className="text-sm">Checking record…</p>
          </div>
        ) : verified ? (
          <div className="rounded-2xl border border-[#BBF7D0] bg-white p-7 sm:p-9" data-testid="verify-result-verified">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#DCFCE7] text-[#15803D]">
                <ShieldCheck size={26} />
              </span>
              <div>
                <h1 className="font-heading text-2xl font-bold text-navy">Verified TenantSeal record</h1>
                <p className="text-sm text-[#475569]">This photo fingerprint belongs to a sealed record.</p>
              </div>
            </div>

            <div className="mt-7 space-y-3">
              <Row icon={CalendarClock} label="Captured" value={fmt(data.captured_at)} testId="verify-captured" />
              <Row icon={Lock} label="Sealed" value={fmt(data.locked_at)} testId="verify-sealed" />
              {data.property_city_only ? (
                <Row icon={MapPin} label="Location" value={data.property_city_only} testId="verify-location" />
              ) : null}
            </div>

            {data.tenant_attestation ? (
              <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="verify-tenant-attestation">
                <p className="text-sm font-medium text-navy">Tenant attestation on file</p>
                {data.tenant_attestation.statement_text && (
                  <p className="mt-1 text-sm text-navy">&ldquo;{data.tenant_attestation.statement_text}&rdquo;</p>
                )}
                <p className="mt-1 text-xs text-[#475569]">
                  Signed by {data.tenant_attestation.signer_name} · {fmt(data.tenant_attestation.signed_at)} · Statement {data.tenant_attestation.statement_version}
                </p>
                <p className="mt-1 text-xs text-[#475569]">Covers evidence types: {(data.tenant_attestation.covered_evidence_types || []).join(", ") || "—"}</p>
                <p className="mt-1 text-[11px] font-mono break-all text-[#94A3B8]">Bound fingerprint: {data.tenant_attestation.seal_fingerprint}</p>
                <p className="mt-1 text-[11px] text-[#94A3B8]">
                  This is the tenant's own attestation — not notarization, identity verification, landlord agreement, or proof the underlying events are true.
                </p>
              </div>
            ) : null}

            <p className="mt-7 text-xs leading-relaxed text-[#94A3B8]">
              To protect privacy, this page never shows the photo, full address, or the person who created the
              record. It only confirms that this fingerprint matches a sealed, timestamped TenantSeal record.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-7 sm:p-9 text-center" data-testid="verify-result-notfound">
            <span className="mx-auto grid place-items-center h-12 w-12 rounded-xl bg-[#F1F5F9] text-[#94A3B8]">
              <ShieldX size={26} />
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold text-navy">Record not found</h1>
            <p className="mt-2 text-sm text-[#475569]">
              We couldn't match this fingerprint to any sealed TenantSeal record.
            </p>
          </div>
        )}

        <footer className="mt-10 py-6 text-center">
          <a href="/" className="text-sm text-[#94A3B8] hover:text-brand">Prepared with TenantSeal</a>
        </footer>
      </main>
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] px-4 py-3" data-testid={testId}>
      <span className="inline-flex items-center gap-2 text-sm font-medium text-[#475569]">
        <Icon size={16} className="text-brand" /> {label}
      </span>
      <span className="text-sm font-semibold text-navy">{value}</span>
    </div>
  );
}
